import clsx from 'clsx';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import BaseContainer from '@commons/containers/BaseContainer';

import styles from './index.module.scss';

/**
 * Type of supported list nodes.
 */

export type ListNode = 'ul' | 'ol';

/**
 * Type of supported list variants.
 */

export type ListVariant = 'square' | 'bullet' | 'dash' | 'number' | 'border' | 'checkmark-circle';

/**
 * List component props.
 *
 * Based on the BaseContainer props.
 */

export type ListProps = ComponentPropsWithRef<typeof BaseContainer> & {
  variant?: ListVariant;
  start?: number;
};

/**
 * List component.
 *
 * Should be used with ListItem to have proper styling.
 */

export const List = (props: ListProps) => {
  const { variant = 'dash', children, start, className, ref, ...rest } = props;

  const as: ListNode = variant === 'number' ? 'ol' : 'ul';
  const classes = clsx(
    'list',
    styles.list,
    `variant-${variant}`,
    styles[`variant-${variant}`],
    className
  );

  return (
    <BaseContainer
      ref={ref}
      as={as}
      className={classes}
      {...(as === 'ol' && start ? { start } : {})}
      {...rest}
    >
      {children}
    </BaseContainer>
  );
};

/**
 * List item component props.
 */

export type ListItemProps = ComponentPropsWithRef<'li'> & {
  children: ReactNode;
  className?: string;
};

/**
 * List item component.
 */

export const ListItem = (props: ListItemProps) => {
  const { children, className, ref, ...rest } = props;

  const classes = clsx(styles.listItem, className);

  return (
    <li ref={ref} className={classes} {...rest}>
      {children}
    </li>
  );
};

// Assigning ListItem to the List namespace.

List.Item = ListItem;

export default List;
